import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import {
  MediaModel,
  IMediaMetadata,
  ILocation,
  ITagAssociationMeta,
  DateParser,
  formatLocation,
  isDateUserDefined,
} from "common247";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors } from "@/constants/ThemeColours";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";

export interface IExtractMediaInfoModalProps {
  visible: boolean;
  media: MediaModel;
  extractedMetadata: IMediaMetadata | null;
  onApply: (appliedFields: {
    date?: string;
    location?: ILocation;
    tags?: Record<string, ITagAssociationMeta>;
    description?: string;
  }) => Promise<void> | void;
  onClose: () => void;
}

interface IFieldComparisonItem {
  id: "date" | "location" | "tags" | "description";
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  currentDisplay: string;
  proposedDisplay: string;
  hasProposedValue: boolean;
  isDifferent: boolean;
}

/**
 * Modal dialog displaying a side-by-side comparison between current media settings
 * and AI/EXIF-extracted proposed settings, allowing the user to select and apply updates.
 */
export function ExtractMediaInfoModal({
  visible,
  media,
  extractedMetadata,
  onApply,
  onClose,
}: IExtractMediaInfoModalProps) {
  const cardBg = useThemeColor({}, "card");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const subtextColor = useThemeColor({}, "tabIconDefault");
  const badgeBg = useThemeColor({}, "inputReadOnly");
  const tintColor = useThemeColor({}, "tint");

  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>({
    date: true,
    location: true,
    tags: true,
    description: true,
  });
  const [isApplying, setIsApplying] = useState(false);

  // Derive proposed structured date from capturedAt
  const proposedDateStr = React.useMemo(() => {
    if (!extractedMetadata?.capturedAt) return "";
    try {
      const d = new Date(extractedMetadata.capturedAt);
      if (isNaN(d.getTime())) return "";
      return DateParser.parse(d.toISOString()).formatted;
    } catch {
      return "";
    }
  }, [extractedMetadata?.capturedAt]);

  // Derive clean current formatted date for accurate equality comparison
  const currentDateFormatted = React.useMemo(() => {
    const rawDate = media.date;
    if (!rawDate) return "";
    try {
      return DateParser.parse(rawDate).formatted;
    } catch {
      return "";
    }
  }, [media.date]);

  // Derive current date display
  const currentDateDisplay = React.useMemo(() => {
    const rawDate = media.date;
    if (!rawDate) return "None";
    try {
      const parsed = DateParser.parse(rawDate);
      const isManual = isDateUserDefined(rawDate);
      return `${parsed.formatted} ${isManual ? "(Manual)" : "(Captured)"}`;
    } catch {
      return String(rawDate);
    }
  }, [media.date]);

  // Derive proposed location label
  const proposedLocationLabel = React.useMemo(() => {
    if (extractedMetadata?.gpsLocationLabel) return extractedMetadata.gpsLocationLabel;
    if (extractedMetadata?.gpsLatitude != null && extractedMetadata?.gpsLongitude != null) {
      return `${extractedMetadata.gpsLatitude.toFixed(4)}°, ${extractedMetadata.gpsLongitude.toFixed(4)}°`;
    }
    return "";
  }, [extractedMetadata?.gpsLocationLabel, extractedMetadata?.gpsLatitude, extractedMetadata?.gpsLongitude]);

  const currentLocationDisplay = formatLocation(media.location) || "None";

  // Derive current tags
  const currentTagsList = React.useMemo(() => {
    if (Array.isArray(media.tags)) return media.tags;
    if (media.tags && typeof media.tags === "object") return Object.keys(media.tags);
    return [];
  }, [media.tags]);

  // Derive proposed tags from AI classifications & metadata
  const proposedTagsList = React.useMemo(() => {
    const combined = new Set<string>();
    if (Array.isArray(extractedMetadata?.tags)) {
      extractedMetadata.tags.forEach((t) => combined.add(t));
    }
    if (Array.isArray(extractedMetadata?.objects)) {
      extractedMetadata.objects.forEach((obj) => combined.add(obj));
    }
    return Array.from(combined);
  }, [extractedMetadata?.tags, extractedMetadata?.objects]);

  const currentTagsDisplay = currentTagsList.length > 0 ? currentTagsList.join(", ") : "None";
  const proposedTagsDisplay = proposedTagsList.length > 0 ? proposedTagsList.join(", ") : "";

  // Derive description
  const currentDescriptionDisplay = media.description || "None";
  const proposedDescriptionDisplay = extractedMetadata?.description || "";

  // Build comparison items
  const comparisonItems: IFieldComparisonItem[] = [
    {
      id: "date",
      label: "Media Date",
      icon: "calendar-outline",
      currentDisplay: currentDateDisplay,
      proposedDisplay: proposedDateStr ? `${proposedDateStr} (Extracted)` : "No date found",
      hasProposedValue: Boolean(proposedDateStr),
      isDifferent: Boolean(proposedDateStr && proposedDateStr !== currentDateFormatted),
    },
    {
      id: "location",
      label: "GPS Location",
      icon: "location-outline",
      currentDisplay: currentLocationDisplay,
      proposedDisplay: proposedLocationLabel || "No location found",
      hasProposedValue: Boolean(proposedLocationLabel),
      isDifferent: Boolean(
        proposedLocationLabel &&
        proposedLocationLabel !== currentLocationDisplay &&
        proposedLocationLabel !== (media.location?.text || "")
      ),
    },
    {
      id: "tags",
      label: "AI Tags & Objects",
      icon: "pricetags-outline",
      currentDisplay: currentTagsDisplay,
      proposedDisplay: proposedTagsDisplay || "No objects detected",
      hasProposedValue: proposedTagsList.length > 0,
      isDifferent: proposedTagsList.some((t) => !currentTagsList.includes(t)),
    },
    {
      id: "description",
      label: "Description",
      icon: "document-text-outline",
      currentDisplay: currentDescriptionDisplay,
      proposedDisplay: proposedDescriptionDisplay || "No description synthesized",
      hasProposedValue: Boolean(proposedDescriptionDisplay),
      isDifferent: Boolean(
        proposedDescriptionDisplay &&
        proposedDescriptionDisplay.trim() !== (media.description || "").trim()
      ),
    },
  ];

  // Auto-select fields when extractedMetadata changes
  useEffect(() => {
    if (visible && extractedMetadata) {
      const initial: Record<string, boolean> = {};
      comparisonItems.forEach((item) => {
        initial[item.id] = item.hasProposedValue && item.isDifferent;
      });
      setSelectedFields(initial);
    }
  }, [visible, extractedMetadata]);

  const toggleField = (id: string) => {
    setSelectedFields((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const selectedCount = Object.values(selectedFields).filter(Boolean).length;

  const handleApply = async () => {
    if (selectedCount === 0) {
      onClose();
      return;
    }
    setIsApplying(true);
    try {
      const applied: {
        date?: string;
        location?: ILocation;
        tags?: Record<string, ITagAssociationMeta>;
        description?: string;
      } = {};

      if (selectedFields.date && proposedDateStr) {
        const parsed = DateParser.parse(proposedDateStr);
        const payload = JSON.stringify({
          ...parsed,
          userDefined: false,
          source: "exif",
        });
        applied.date = payload;
      }

      if (selectedFields.location && (extractedMetadata?.gpsLatitude != null || extractedMetadata?.gpsLongitude != null || proposedLocationLabel)) {
        applied.location = {
          latitude: extractedMetadata?.gpsLatitude,
          longitude: extractedMetadata?.gpsLongitude,
          text: proposedLocationLabel || undefined,
        };
      }

      if (selectedFields.tags && proposedTagsList.length > 0) {
        const mergedTags: Record<string, ITagAssociationMeta> = {};
        // Preserve current tags
        if (media.tags && typeof media.tags === "object" && !Array.isArray(media.tags)) {
          Object.assign(mergedTags, media.tags);
        } else if (Array.isArray(media.tags)) {
          media.tags.forEach((t) => {
            mergedTags[t] = { tagId: t, addedAt: Date.now(), addedBy: media.ownerId || "system" };
          });
        }
        // Add proposed tags
        proposedTagsList.forEach((t) => {
          if (!mergedTags[t]) {
            mergedTags[t] = { tagId: t, addedAt: Date.now(), addedBy: "ai-extractor" };
          }
        });
        applied.tags = mergedTags;
      }

      if (selectedFields.description && proposedDescriptionDisplay) {
        applied.description = proposedDescriptionDisplay;
      }

      await onApply(applied);
      onClose();
    } catch (err) {
      console.error("[ExtractMediaInfoModal] Failed to apply settings:", err);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={[styles.modalCard, { backgroundColor: cardBg, borderColor }]}>
          <ThemedProgressBar loading={isApplying} />
          {/* Header */}
          <View style={[styles.headerRow, { borderBottomColor: borderColor }]}>
            <View style={styles.headerTitleRow}>
              <View style={[styles.sparkleBadge, { backgroundColor: BrandColors.sungoldOrange.hex }]}>
                <Ionicons name="sparkles" size={16} color="#FFFFFF" />
              </View>
              <View>
                <Text style={[styles.headerTitle, { color: textColor }]}>
                  Extract Media Info
                </Text>
                <Text style={[styles.headerSubtitle, { color: subtextColor }]}>
                  Review extracted metadata before applying updates
                </Text>
              </View>
            </View>
            <TouchableOpacity
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Close extract modal"
            >
              <Ionicons name="close" size={24} color={subtextColor} />
            </TouchableOpacity>
          </View>

          {/* Comparison Table / List */}
          <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
            <Text style={[styles.sectionHeading, { color: textColor }]}>
              Comparison Settings
            </Text>

            {comparisonItems.map((item) => {
              const isSelected = !!selectedFields[item.id];
              const canToggle = item.hasProposedValue;

              return (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    styles.comparisonRowCard,
                    {
                      borderColor: isSelected ? tintColor : borderColor,
                      backgroundColor: isSelected ? badgeBg : "transparent",
                    },
                  ]}
                  onPress={() => canToggle && toggleField(item.id)}
                  disabled={!canToggle}
                  activeOpacity={0.7}
                  testID={`extract-row-${item.id}`}
                >
                  <View style={styles.rowTopHeader}>
                    <View style={styles.fieldLabelRow}>
                      <Ionicons name={item.icon} size={16} color={tintColor} style={{ marginRight: 6 }} />
                      <Text style={[styles.fieldLabel, { color: textColor }]}>
                        {item.label}
                      </Text>
                    </View>
                    <View style={styles.checkboxContainer}>
                      {canToggle ? (
                        <Ionicons
                          name={isSelected ? "checkbox" : "square-outline"}
                          size={20}
                          color={isSelected ? tintColor : subtextColor}
                        />
                      ) : (
                        <Text style={[styles.noValueTag, { color: subtextColor }]}>
                          No Data
                        </Text>
                      )}
                    </View>
                  </View>

                  <View style={styles.valuesGrid}>
                    <View style={styles.valueColumn}>
                      <Text style={[styles.columnHeader, { color: subtextColor }]}>
                        Current Setting:
                      </Text>
                      <Text style={[styles.currentValueText, { color: textColor }]} numberOfLines={2}>
                        {item.currentDisplay}
                      </Text>
                    </View>

                    <View style={styles.arrowDivider}>
                      <Ionicons name="arrow-forward" size={14} color={subtextColor} />
                    </View>

                    <View style={styles.valueColumn}>
                      <Text style={[styles.columnHeader, { color: item.hasProposedValue ? tintColor : subtextColor }]}>
                        Proposed Extracted:
                      </Text>
                      <Text
                        style={[
                          styles.proposedValueText,
                          {
                            color: item.hasProposedValue ? textColor : subtextColor,
                            fontWeight: item.hasProposedValue ? "600" : "400",
                          },
                        ]}
                        numberOfLines={2}
                      >
                        {item.proposedDisplay}
                      </Text>
                    </View>
                  </View>
                </TouchableOpacity>
              );
            })}

            {/* Technical Metadata Specs Box */}
            {(extractedMetadata?.width || extractedMetadata?.height || extractedMetadata?.durationMs) && (
              <View style={[styles.specsBox, { backgroundColor: badgeBg, borderColor }]}>
                <Text style={[styles.specsTitle, { color: textColor }]}>
                  Asset Dimensions & Duration
                </Text>
                <Text style={[styles.specsText, { color: subtextColor }]}>
                  {extractedMetadata.width && extractedMetadata.height
                    ? `Dimensions: ${extractedMetadata.width} × ${extractedMetadata.height} px`
                    : ""}
                  {extractedMetadata.durationMs
                    ? ` • Duration: ${(extractedMetadata.durationMs / 1000).toFixed(1)}s`
                    : ""}
                </Text>
              </View>
            )}
          </ScrollView>

          {/* Footer Actions */}
          <View style={[styles.footerRow, { borderTopColor: borderColor }]}>
            <ThemedButton
              title="Cancel"
              variant="minimal"
              onPress={onClose}
              disabled={isApplying}
              style={styles.cancelBtn}
            />
            <ThemedButton
              title={isApplying ? "Applying..." : `Apply Selected (${selectedCount})`}
              variant="primary"
              onPress={handleApply}
              disabled={isApplying || selectedCount === 0}
              icon={isApplying ? undefined : "checkmark-circle-outline"}
              testID="extract-apply-btn"
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 540,
    maxHeight: "85%",
    borderRadius: 14,
    borderWidth: 1,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 10,
    elevation: 8,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  headerTitleRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  sparkleBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  headerSubtitle: {
    fontSize: 12,
    marginTop: 2,
  },
  scrollArea: {
    maxHeight: 460,
  },
  scrollContent: {
    padding: 16,
    gap: 12,
  },
  sectionHeading: {
    fontSize: 13,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  comparisonRowCard: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
  },
  rowTopHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  fieldLabelRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "600",
  },
  checkboxContainer: {
    padding: 2,
  },
  noValueTag: {
    fontSize: 11,
    fontStyle: "italic",
  },
  valuesGrid: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  valueColumn: {
    flex: 1,
  },
  arrowDivider: {
    paddingHorizontal: 8,
  },
  columnHeader: {
    fontSize: 11,
    fontWeight: "500",
    marginBottom: 2,
  },
  currentValueText: {
    fontSize: 13,
  },
  proposedValueText: {
    fontSize: 13,
  },
  specsBox: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    marginTop: 4,
  },
  specsTitle: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 2,
  },
  specsText: {
    fontSize: 11,
  },
  footerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    gap: 10,
  },
  cancelBtn: {
    marginRight: 4,
  },
});
